import type { LapDiffPoint, LapDiffMetricStats } from '@/types/telemetry';
import { projectToLocalMeters } from '@/lib/map/mapGeometry';

export type LapDiffMetric = 'time' | 'speed' | 'rpm' | 'acceleration';

export interface LapDiffStats {
  avgDiff: number;
  maxDiff: number;
  pctABetter: number;
  pctBBetter: number;
  unit: string;
}

export interface LapDiffHoverInfo {
  sampleIndex: number;
  latRaw: number;
  lonRaw: number;
  valueA: number;
  valueB: number;
  diff: number;
  unit: string;
  segmentIndex?: number;
  segmentTimeDiffMs?: number;
  cumulativeTimeDiffMs?: number;
  segTimeAMs?: number;
  segTimeBMs?: number;
  cumTimeAMs?: number;
  cumTimeBMs?: number;
}

export interface LapDiffTimeDiff {
  fasterLap: 'A' | 'B' | 'tie';
  diffMs: number;
  lapATimeMs: number;
  lapBTimeMs: number;
  isSegment: boolean;
  segmentSpan?: [number, number];
}

export interface PointTimeSeriesEntry {
  progress: number;
  distanceM: number;
  lat: number;
  lng: number;
  segTimeAMs: number;
  segTimeBMs: number;
  cumTimeAMs: number;
  cumTimeBMs: number;
  /** A - B per-segment time delta in ms (< 0 means A gained time, > 0 means A lost time) */
  segDiffAMs: number;
  /** A - B cumulative time delta in ms (< 0 means A is ahead, > 0 means A is behind) */
  cumDiffAMs: number;
  /** A - B speed delta in km/h */
  speedDiffKmh: number;
  /** A - B RPM delta */
  rpmDiff: number;
  /** A - B acceleration delta in m/s² */
  accelDiff: number;
}

export const LAP_COLORS = [
  '#ef4444', '#3b82f6', '#f59e0b', '#10b981',
  '#38bdf8', '#f472b6', '#a3e635', '#fb923c',
  '#c084fc', '#2dd4bf',
];

export const LAP_A_RGB: [number, number, number] = [239, 68, 68];  // #ef4444 (Lap A: red)
export const LAP_B_RGB: [number, number, number] = [59, 130, 246]; // #3b82f6 (Lap B: blue)

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

/**
 * Computes an RGB color indicating which lap is faster/better on a segment:
 * - aAdvantage > 0 (Lap A is faster / higher metric) -> blends toward Lap A color (#ef4444, Red)
 * - aAdvantage < 0 (Lap B is faster / higher metric) -> blends toward Lap B color (#3b82f6, Blue)
 * - aAdvantage == 0 -> neutral grey rgb(55, 55, 55)
 */
export function getDeviationColorRgb(
  aAdvantage: number,
  maxAbsDiff: number,
  rgbA: [number, number, number] = LAP_A_RGB,
  rgbB: [number, number, number] = LAP_B_RGB,
): [number, number, number] {
  const safeMax = Math.max(maxAbsDiff, 0.001);
  const intensity = Math.min(Math.abs(aAdvantage) / safeMax, 1);
  const boosted = Math.sqrt(intensity);
  const grey = 55;
  const targetRgb = aAdvantage >= 0 ? rgbA : rgbB;
  return [
    Math.round(grey + (targetRgb[0] - grey) * boosted),
    Math.round(grey + (targetRgb[1] - grey) * boosted),
    Math.round(grey + (targetRgb[2] - grey) * boosted),
  ];
}

/**
 * Extracts or computes calibrated per-segment and cumulative time series across all aligned track points.
 */
export function computePointTimeSeries(
  points: LapDiffPoint[],
  lapATimeMs: number,
  lapBTimeMs: number,
): PointTimeSeriesEntry[] {
  const n = points.length;
  if (n === 0) return [];

  // Build monotonic distanceM array
  const distances = new Array<number>(n).fill(0);
  let runningDist = 0;
  for (let i = 1; i < n; i++) {
    const dReported = (points[i].distanceM ?? 0) - (points[i - 1].distanceM ?? 0);
    if (dReported > 0) {
      runningDist += dReported;
    } else {
      const p0 = { lat: points[i - 1].latitudeRaw / 1e6, lng: points[i - 1].longitudeRaw / 1e6 };
      const p1 = { lat: points[i].latitudeRaw / 1e6, lng: points[i].longitudeRaw / 1e6 };
      const delta = projectToLocalMeters(p1, p0);
      runningDist += Math.hypot(delta.x, delta.y);
    }
    distances[i] = runningDist;
  }

  const hasBackendTimes = points.some(
    p => (p.lapA?.segmentTimeMs ?? 0) > 0 || (p.lapB?.segmentTimeMs ?? 0) > 0
  );

  if (hasBackendTimes) {
    return points.map((p, i) => {
      const segA = p.lapA?.segmentTimeMs ?? 0;
      const segB = p.lapB?.segmentTimeMs ?? 0;
      const cumA = p.lapA?.cumulativeTimeMs ?? 0;
      const cumB = p.lapB?.cumulativeTimeMs ?? 0;
      return {
        progress: p.progress,
        distanceM: distances[i],
        lat: p.latitudeRaw / 1e6,
        lng: p.longitudeRaw / 1e6,
        segTimeAMs: segA,
        segTimeBMs: segB,
        cumTimeAMs: cumA,
        cumTimeBMs: cumB,
        segDiffAMs: p.delta?.segmentTimeMs ?? (segA - segB),
        cumDiffAMs: p.delta?.cumulativeTimeMs ?? (cumA - cumB),
        speedDiffKmh: p.delta?.speedKmh ?? (p.lapA.speedKmh - p.lapB.speedKmh),
        rpmDiff: p.delta?.rpm ?? (p.lapA.rpm - p.lapB.rpm),
        accelDiff: p.delta?.acceleration ?? (p.lapA.acceleration - p.lapB.acceleration),
      };
    });
  }

  // Client-side fallback integration using segment length and average speed
  const rawSegA = new Array<number>(n).fill(0);
  const rawSegB = new Array<number>(n).fill(0);
  let sumA = 0;
  let sumB = 0;
  for (let i = 1; i < n; i++) {
    const dist = Math.max(0.1, distances[i] - distances[i - 1]);
    const vA = Math.max((points[i - 1].lapA.speedKmh + points[i].lapA.speedKmh) * 0.5, 5.0) / 3.6;
    const vB = Math.max((points[i - 1].lapB.speedKmh + points[i].lapB.speedKmh) * 0.5, 5.0) / 3.6;
    rawSegA[i] = (dist / vA) * 1000;
    rawSegB[i] = (dist / vB) * 1000;
    sumA += rawSegA[i];
    sumB += rawSegB[i];
  }

  const scaleA = lapATimeMs > 0 && sumA > 0 ? lapATimeMs / sumA : 1;
  const scaleB = lapBTimeMs > 0 && sumB > 0 ? lapBTimeMs / sumB : 1;

  let cumA = 0;
  let cumB = 0;
  return points.map((p, i) => {
    const segA = rawSegA[i] * scaleA;
    const segB = rawSegB[i] * scaleB;
    cumA += segA;
    cumB += segB;
    return {
      progress: p.progress,
      distanceM: distances[i],
      lat: p.latitudeRaw / 1e6,
      lng: p.longitudeRaw / 1e6,
      segTimeAMs: segA,
      segTimeBMs: segB,
      cumTimeAMs: cumA,
      cumTimeBMs: cumB,
      segDiffAMs: segA - segB,
      cumDiffAMs: cumA - cumB,
      speedDiffKmh: p.delta?.speedKmh ?? (p.lapA.speedKmh - p.lapB.speedKmh),
      rpmDiff: p.delta?.rpm ?? (p.lapA.rpm - p.lapB.rpm),
      accelDiff: p.delta?.acceleration ?? (p.lapA.acceleration - p.lapB.acceleration),
    };
  });
}

const UNIT_MAP: Record<LapDiffMetric, string> = {
  time: 'ms',
  speed: 'km/h',
  rpm: 'RPM',
  acceleration: 'm/s²',
};

export function getMetricUnit(metric: LapDiffMetric): string {
  return UNIT_MAP[metric] || '';
}

/** Format backend-computed LapDiffMetricStats for display. */
export function formatLapDiffStats(
  metric: LapDiffMetric,
  viewLap: 'A' | 'B',
  metricStats?: LapDiffMetricStats,
): LapDiffStats {
  const unit = getMetricUnit(metric);
  if (!metricStats) {
    return { avgDiff: 0, maxDiff: 0, pctABetter: 0, pctBBetter: 0, unit };
  }
  return {
    avgDiff: metricStats.avgAbsDiff ?? 0,
    maxDiff: metricStats.maxAbsDiff ?? 0,
    pctABetter: viewLap === 'A' ? (metricStats.pctABetter ?? 0) : (metricStats.pctBBetter ?? 0),
    pctBBetter: viewLap === 'A' ? (metricStats.pctBBetter ?? 0) : (metricStats.pctABetter ?? 0),
    unit,
  };
}

/** Format backend-computed full-lap or segment time diff for display. */
export function formatLapDiffTimeDiff(
  lapATimeMs: number,
  lapBTimeMs: number,
  totalTimeDiffMs: number,
  fasterLap: string,
  isSegment?: boolean,
  segmentLapATimeMs?: number,
  segmentLapBTimeMs?: number,
  segmentTimeDiffMs?: number,
  segmentFasterLap?: string,
): LapDiffTimeDiff {
  if (isSegment) {
    return {
      fasterLap: ((segmentFasterLap as 'A' | 'B' | 'tie') || 'tie'),
      diffMs: segmentTimeDiffMs ?? 0,
      lapATimeMs: segmentLapATimeMs ?? 0,
      lapBTimeMs: segmentLapBTimeMs ?? 0,
      isSegment: true,
    };
  }
  return {
    fasterLap: ((fasterLap as 'A' | 'B' | 'tie') || 'tie'),
    diffMs: totalTimeDiffMs ?? Math.abs(lapATimeMs - lapBTimeMs),
    lapATimeMs,
    lapBTimeMs,
    isSegment: false,
  };
}

